import React, { useRef, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import CameraVideoPlayer, { CameraVideoPlayerRef } from './CameraVideoPlayer';
import XTAMSpinner from './XTAMSpinner';
import type { PlayerSlot, PlaybackPlayerRef } from '../hooks/usePlayerState';
import { recordingTimelineOffsetSec, wallClockMsFromPlaybackVideo } from '../hooks/usePlayerState';
import type { EvidencePhoto } from './RecordingsSpoaModal';
import toast from 'react-hot-toast';
import './CameraDropZone.css';
import { NetworkPingIndicator } from './NetworkPingIndicator';

const SITE_DROP_TYPE = 'application/x-vms-site';

type EvidenceStripVariant = 'embedded' | 'overlay';

function normalizeDisplayText(s: string): string {
  return s.trim().replace(/\s+/g, ' ').toLocaleLowerCase('es-ES');
}

function cameraNameIncludesSite(cameraName: string, siteName?: string): boolean {
  if (!siteName) return false;
  const camera = normalizeDisplayText(cameraName);
  const site = normalizeDisplayText(siteName);
  return Boolean(site) && (camera === site || camera.startsWith(`${site} `));
}

function displayRecordingErrorMessage(error?: string): string {
  const raw = (error ?? '').trim();
  if (!raw) return 'Error al cargar la grabación';
  if (/no recording for this time range/i.test(raw)) {
    return 'No hay grabación para este rango de tiempo';
  }
  if (/no recordings available for the specified time range/i.test(raw)) {
    return 'No hay grabaciones disponibles para el rango de tiempo especificado';
  }
  return raw;
}

function SlotEvidenceStrip({
  photos,
  onRemove,
  variant,
}: {
  photos: EvidencePhoto[];
  onRemove: (id: string) => void;
  variant: EvidenceStripVariant;
}) {
  if (photos.length === 0) return null;
  return (
    <div
      className={`camera-drop-zone__evidence-strip ${
        variant === 'overlay' ? 'camera-drop-zone__evidence-strip--overlay' : ''
      }`}
      role="region"
      aria-label="Capturas de evidencia de esta cámara"
    >
      <div className="camera-drop-zone__evidence-strip-head">
        <span className="camera-drop-zone__evidence-strip-title">Evidencias</span>
        <span className="camera-drop-zone__evidence-strip-count">{photos.length}</span>
      </div>
      <div className="camera-drop-zone__evidence-scroll">
        {photos.map((p) => (
          <div key={p.id} className="camera-drop-zone__evidence-thumb-wrap">
            <img src={p.dataUrl} alt="" className="camera-drop-zone__evidence-thumb" />
            <button
              type="button"
              className="camera-drop-zone__evidence-remove"
              onClick={() => onRemove(p.id)}
              title="Quitar esta captura"
              aria-label="Quitar esta captura"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export interface CameraDropZoneProps {
  slot: PlayerSlot;
  mode: 'PLAYING' | 'PAUSED' | 'STOPPED';
  playbackSpeed: number;
  slotIndex: number;
  onDrop: (slotId: number, camera: NonNullable<PlayerSlot['camera']>) => void;
  /** Al soltar un sitio, se llama con los cameraIds para llenar varios slots */
  onDropSite?: (slotId: number, cameraIds: string[]) => void;
  onRemove: (slotId: number) => void;
  onRegisterPlayer: (slotId: number, ref: React.RefObject<PlaybackPlayerRef | null>) => void;
  onUnregisterPlayer: (slotId: number) => void;
  onRetry?: (slotId: number) => void;
  onError?: (message: string) => void;
  expandedSlotId: number | null;
  onExpand: (slotId: number | null) => void;
  draggedCamera: { id: string; name: string } | null;
  /** Fallback cuando el drop desde el portal no entrega dataTransfer.getData (algunos navegadores) */
  getDraggedCameraFromSchema?: () => { id: string; name: string } | null;
  /** Solo grabaciones: botón para capturar fotograma como evidencia */
  onEvidenceCapture?: (payload: {
    slotId: number;
    cameraId: string;
    dataUrl: string;
    cameraName: string;
    videoTimeSec: number;
    wallClockMs: number;
  }) => void;
  /** Capturas de esta cámara (grabaciones): miniaturas bajo el video */
  slotEvidencePhotos?: EvidencePhoto[];
  onEvidenceRemove?: (photoId: string) => void;
  /**
   * Tras cerrar el modal ampliado: posición HLS (s) para el tile; evita remontar con el offset del
   * prepare y chocar con el sync multi-cámara (buffering infinito).
   */
  postExpandResumeHlsSec?: number;
  /** Limpia postExpandResume al alinear el seek o al cambiar la ventana HLS (nuevo prepare). */
  onClearPostExpandResume?: (slotId: number) => void;
  /** Tras reinicio de buffer HLS (p. ej. bajar velocidad): extiende ventana sin seek de sync. */
  onHlsBufferReset?: () => void;
  /**
   * Ancla de frame / seek inicial: hora de pared coherente con el puntero del timeline (p. ej. si el usuario
   * movió el scrub dentro de la ventana HLS). Si no se pasa, se usa `slot.playback.timelineAnchorWallTime`.
   */
  recordingDisplayAnchorWallTime?: Date | null;
  /**
   * Contenedor DOM al que se teletransporta el `<video>` cuando este slot está maximizado en el modal de grabaciones.
   * Mantiene la misma instancia de Hls.js y el buffer (m3u8/ts) al alternar grid ↔ modal.
   */
  expandedPlayerHost?: HTMLDivElement | null;
  /** Estado de conectividad de red (ping al servidor de video) */
  networkHealth?: { quality: 'good' | 'slow' | 'offline' | 'unknown'; pingMs: number | null };
  /** Cámaras activas en la grilla (para ajustar prefetch HLS según concurrencia) */
  activeCameraCount?: number;
}

export const CameraDropZone: React.FC<CameraDropZoneProps> = ({
  slot,
  mode,
  playbackSpeed,
  slotIndex,
  onDrop,
  onDropSite,
  onRemove,
  onRegisterPlayer,
  onUnregisterPlayer,
  onRetry,
  onError,
  expandedSlotId,
  onExpand,
  draggedCamera,
  getDraggedCameraFromSchema,
  onEvidenceCapture,
  slotEvidencePhotos = [],
  onEvidenceRemove,
  postExpandResumeHlsSec,
  onClearPostExpandResume,
  onHlsBufferReset,
  recordingDisplayAnchorWallTime,
  expandedPlayerHost,
  networkHealth,
  activeCameraCount,
}) => {
  const slotRef = useRef<React.RefObject<CameraVideoPlayerRef | null> | null>(null);
  if (!slotRef.current) slotRef.current = React.createRef<CameraVideoPlayerRef | null>();
  const playerRef = slotRef.current as React.RefObject<CameraVideoPlayerRef | null>;
  const [playbackSeconds, setPlaybackSeconds] = useState(0);
  const [streamVisualStatus, setStreamVisualStatus] = useState<'loading' | 'playing' | 'error' | 'offline' | 'placeholder'>('loading');
  const [loadSpeedBytesPerSec, setLoadSpeedBytesPerSec] = useState<number | null>(null);
  const [evidenceCaptureFlash, setEvidenceCaptureFlash] = useState(false);
  const evidenceCaptureFlashTimerRef = useRef<number | null>(null);
  // Host estable dentro del grid que recibe el `<video>` portaled cuando el slot NO está maximizado.
  // Tener un nodo siempre montado evita que el portal pierda destino durante la transición grid ↔ modal,
  // que destruiría el `<video>` (y por tanto su buffer HLS).
  const [inlineHostEl, setInlineHostEl] = useState<HTMLDivElement | null>(null);
  // Host DOM estable (no gestionado por React) en el que SIEMPRE vive el portal del `<video>`.
  // Lo reparenta-mos vía DOM appendChild entre inlineHostEl y expandedPlayerHost al maximizar/minimizar,
  // así React no recrea el portal (containerInfo no cambia) y el `<video>` + Hls.js se preservan.
  const [stableHostEl, setStableHostEl] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    const host = document.createElement('div');
    host.className = 'camera-drop-zone__stable-host';
    setStableHostEl(host);
    return () => {
      host.remove();
    };
  }, []);

  const playbackTimeLabel = useMemo(() => {
    if (!slot.playback) return '--:--:--';
    const wallMs = wallClockMsFromPlaybackVideo(slot.playback, playbackSeconds);
    const current = new Date(wallMs);
    return current.toLocaleTimeString('es-ES', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  }, [slot.playback, playbackSeconds]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = onDropSite ? 'copy' : 'move';
  }, [onDropSite]);

  const handleEvidenceClick = useCallback(() => {
    if (!onEvidenceCapture || !slot.camera || !slot.playback) return;
    const cap = playerRef.current?.captureFrame?.() ?? null;
    if (!cap) {
      toast.error('No se pudo capturar la imagen', { duration: 2500 });
      return;
    }
    const rawVideoSec = playerRef.current?.getCurrentTime() ?? 0;
    const pb = slot.playback;
    const wallClockMs = wallClockMsFromPlaybackVideo(pb, rawVideoSec);
    const videoTimeSec = recordingTimelineOffsetSec(pb, rawVideoSec);
    onEvidenceCapture({
      slotId: slot.id,
      cameraId: String(slot.camera.id),
      dataUrl: cap,
      cameraName: slot.camera.name,
      videoTimeSec,
      wallClockMs,
    });
    if (evidenceCaptureFlashTimerRef.current != null) {
      window.clearTimeout(evidenceCaptureFlashTimerRef.current);
    }
    setEvidenceCaptureFlash(true);
    evidenceCaptureFlashTimerRef.current = window.setTimeout(() => {
      setEvidenceCaptureFlash(false);
      evidenceCaptureFlashTimerRef.current = null;
    }, 800);
  }, [onEvidenceCapture, slot.id, slot.camera, slot.playback, playerRef]);

  React.useEffect(() => {
    return () => {
      if (evidenceCaptureFlashTimerRef.current != null) {
        window.clearTimeout(evidenceCaptureFlashTimerRef.current);
      }
    };
  }, []);

  const evidenceStripOverlay =
    onEvidenceRemove && slotEvidencePhotos.length > 0 ? (
      <SlotEvidenceStrip photos={slotEvidencePhotos} onRemove={onEvidenceRemove} variant="overlay" />
    ) : null;
  const evidenceStripEmbedded =
    onEvidenceRemove && slotEvidencePhotos.length > 0 ? (
      <SlotEvidenceStrip photos={slotEvidencePhotos} onRemove={onEvidenceRemove} variant="embedded" />
    ) : null;

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      // Prioridad 1: cámara desde panel (portal) — getData suele venir vacío en algunos navegadores
      if (getDraggedCameraFromSchema) {
        const camera = getDraggedCameraFromSchema();
        if (camera?.id != null && camera?.name != null) {
          onDrop(slot.id, camera);
          return;
        }
      }
      // Prioridad 2: sitio completo
      let siteData = e.dataTransfer.getData(SITE_DROP_TYPE);
      if (!siteData && onDropSite) {
        const text = e.dataTransfer.getData('text/plain');
        if (typeof text === 'string' && text.startsWith('vms-site:')) {
          siteData = text.slice(9);
        }
      }
      if (siteData && onDropSite) {
        try {
          const data = JSON.parse(siteData) as { siteId?: string; siteName?: string; cameraIds: string[] };
          if (Array.isArray(data.cameraIds) && data.cameraIds.length > 0) {
            onDropSite(slot.id, data.cameraIds);
            return;
          }
        } catch {
          // fall through to camera drop
        }
      }
      // Prioridad 3: cámara desde dataTransfer (búsqueda u otra zona)
      let camJson = e.dataTransfer.getData('application/json');
      if (!camJson) {
        const text = e.dataTransfer.getData('text/plain');
        if (typeof text === 'string' && text.startsWith('vms-camera:')) {
          camJson = text.slice(11);
        }
      }
      if (!camJson) return;
      try {
        const camera = JSON.parse(camJson) as NonNullable<PlayerSlot['camera']>;
        if (camera?.id && camera?.name) onDrop(slot.id, camera);
      } catch {
        // ignore
      }
    },
    [slot.id, onDrop, onDropSite, getDraggedCameraFromSchema]
  );

  React.useEffect(() => {
    if (slot.camera && slot.streamStatus === 'READY') {
      onRegisterPlayer(slot.id, playerRef);
    }
    return () => onUnregisterPlayer(slot.id);
  }, [slot.id, slot.camera, slot.streamStatus, onRegisterPlayer, onUnregisterPlayer]);

  // Reparenta el stableHostEl (que aloja el portal del `<video>`) entre el host inline del grid y
  // el host del modal, vía DOM directo. React no recrea el portal porque su containerInfo
  // (stableHostEl) no cambia, así que Hls.js y el currentTime del vídeo se preservan al maximizar/minimizar.
  const isExpanded = expandedSlotId === slot.id;
  useLayoutEffect(() => {
    if (!stableHostEl) return;
    const target = isExpanded
      ? expandedPlayerHost ?? inlineHostEl
      : inlineHostEl ?? expandedPlayerHost;
    if (target && stableHostEl.parentElement !== target) {
      target.appendChild(stableHostEl);
    }
  }, [isExpanded, stableHostEl, inlineHostEl, expandedPlayerHost]);

  const isPlaying = mode === 'PLAYING' && slot.streamStatus === 'READY';
  /** Incluye cámara y estado para forzar remount / reset de UI al sustituir cámara en el mismo slot. */
  const playbackKey = slot.playback
    ? `${slot.id}-${slot.camera?.id ?? ''}-${slot.playback.playlistUrl}-${slot.playback.startTime.getTime()}`
    : slot.camera
      ? `${slot.id}-${slot.camera.id}-${slot.streamStatus}`
      : `slot-${slot.id}-idle`;

  const playbackKeyForResumeRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (playbackKeyForResumeRef.current === null) {
      playbackKeyForResumeRef.current = playbackKey;
      return;
    }
    if (playbackKeyForResumeRef.current !== playbackKey) {
      playbackKeyForResumeRef.current = playbackKey;
      onClearPostExpandResume?.(slot.id);
    }
  }, [playbackKey, slot.id, onClearPostExpandResume]);

  const rawRecordingHlsStartOffset =
    postExpandResumeHlsSec != null && Number.isFinite(postExpandResumeHlsSec) && postExpandResumeHlsSec >= 0
      ? postExpandResumeHlsSec
      : slot.playback?.startOffsetSeconds ?? 0;
  // Redondear para no re-disparar el efecto de seek ancla en CameraVideoPlayer por float jitter.
  const recordingHlsStartOffset = Math.round(Number(rawRecordingHlsStartOffset) * 1000) / 1000;

  // Evita que el pill de estado quede en CARGANDO/BUFFERING de la grabación anterior al quitar o cambiar cámara.
  React.useEffect(() => {
    setPlaybackSeconds(0);
    if (!slot.camera) {
      setStreamVisualStatus('placeholder');
      return;
    }
    if (slot.streamStatus === 'PREPARING') {
      setStreamVisualStatus('loading');
      return;
    }
    if (slot.streamStatus === 'ERROR') {
      setStreamVisualStatus('error');
      return;
    }
    if (slot.streamStatus === 'READY' && slot.playback) {
      setStreamVisualStatus('loading');
    }
  }, [playbackKey, slot.camera, slot.streamStatus]);

  if (!slot.camera) {
    return (
      <div
        className={`camera-drop-zone camera-drop-zone--idle ${draggedCamera ? 'camera-drop-zone--drop-target' : ''}`}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        data-slot-id={slot.id}
      >
        <div className="camera-drop-zone__empty">
          <div className="camera-drop-zone__icon" aria-hidden>
            <svg viewBox="0 0 36 36" fill="currentColor">
              <rect x="4" y="10" width="20" height="16" rx="3" opacity="0.55"/>
              <path d="M24 14l8-5v18l-8-5z" opacity="0.55"/>
            </svg>
          </div>
          <span className="camera-drop-zone__text">Arrastra una cámara</span>
          <span className="camera-drop-zone__number">Slot {slotIndex + 1}</span>
        </div>
      </div>
    );
  }

  if (slot.streamStatus === 'PREPARING') {
    return (
      <div className="camera-drop-zone camera-drop-zone--preparing" data-slot-id={slot.id} onDragOver={handleDragOver} onDrop={handleDrop}>
        <div className="camera-drop-zone__content">
          <div className="camera-drop-zone__xtam-spinner-wrap">
            <XTAMSpinner variant="small" message={
              networkHealth?.quality === 'offline'
                ? 'Sin conexión al servidor de video'
                : networkHealth?.quality === 'slow'
                  ? `Conexión lenta (ping: ${networkHealth.pingMs}ms)`
                  : 'Preparando stream…'
            } />
          </div>
          <div className="camera-drop-zone__info">
            <span className="camera-drop-zone__name">{slot.camera.name}</span>
            {networkHealth && (
              <NetworkPingIndicator
                health={networkHealth}
                size={13}
                showLabel
                className="camera-drop-zone__ping camera-drop-zone__ping--prep"
              />
            )}
          </div>
          <button
            type="button"
            className="camera-drop-zone__remove"
            onClick={() => onRemove(slot.id)}
            title="Quitar cámara"
          >
            ×
          </button>
          {evidenceStripOverlay}
        </div>
      </div>
    );
  }

  if (slot.streamStatus === 'ERROR') {
    const siteName = slot.camera.site?.name || '';
    const shouldShowSite = siteName && !cameraNameIncludesSite(slot.camera.name, siteName);
    return (
      <div className="camera-drop-zone camera-drop-zone--error" data-slot-id={slot.id} onDragOver={handleDragOver} onDrop={handleDrop}>
        <div className="camera-drop-zone__content">
          <div className="camera-drop-zone__error-icon" aria-hidden>⚠</div>
          <span className="camera-drop-zone__error-message">{displayRecordingErrorMessage(slot.error)}</span>
          <div className="camera-drop-zone__info">
            {shouldShowSite && <span className="camera-drop-zone__site">{siteName}</span>}
            <span className="camera-drop-zone__name">{slot.camera.name}</span>
          </div>
          {onRetry && (
            <button type="button" className="camera-drop-zone__retry" onClick={() => onRetry(slot.id)}>
              Reintentar
            </button>
          )}
          <button
            type="button"
            className="camera-drop-zone__remove"
            onClick={() => onRemove(slot.id)}
            title="Quitar cámara"
          >
            ×
          </button>
          {evidenceStripOverlay}
        </div>
      </div>
    );
  }

  if (slot.streamStatus === 'READY' && slot.playback) {
    const videoArea = (
      <div
        className="camera-drop-zone__video-area"
        data-expanded={isExpanded || undefined}
        onClick={isExpanded ? (e) => e.stopPropagation() : undefined}
      >
        <CameraVideoPlayer
          key={playbackKey}
          ref={playerRef}
          cameraId={slot.camera.id}
          cameraName={slot.camera.name}
          hlsUrl={slot.playback.playlistUrl}
          disableLiveFallback
          placeholderMessage=""
          recordingStartTime={slot.playback.startTime}
          recordingAnchorWallTime={
            postExpandResumeHlsSec != null
              ? undefined
              : (recordingDisplayAnchorWallTime ?? slot.playback.timelineAnchorWallTime)
          }
          startOffsetSeconds={recordingHlsStartOffset}
          isPlaying={isPlaying}
          playbackRate={playbackSpeed}
          onError={onError}
          onPlaybackTimeUpdate={setPlaybackSeconds}
          onStreamStatusChange={setStreamVisualStatus}
          onRecordingSeekAnchorApplied={
            postExpandResumeHlsSec != null ? () => onClearPostExpandResume?.(slot.id) : undefined
          }
          onHlsBufferReset={onHlsBufferReset}
          activeCameraCount={activeCameraCount}
          onLoadSpeedChange={setLoadSpeedBytesPerSec}
        />
        {!isExpanded && (
          <>
            <div className="camera-drop-zone__overlay-top">
              <div className="camera-drop-zone__camera-label">
                <span className="camera-drop-zone__name">{slot.camera.name}</span>
                {networkHealth && (
                  <NetworkPingIndicator
                    health={networkHealth}
                    loadSpeedBytesPerSec={loadSpeedBytesPerSec}
                    size={13}
                    className="camera-drop-zone__ping"
                  />
                )}
              </div>
              <div className="camera-drop-zone__overlay-actions">
                {onEvidenceCapture && (streamVisualStatus === 'playing' || streamVisualStatus === 'loading') && (
                  <button
                    type="button"
                    className={`camera-drop-zone__evidence-capture${evidenceCaptureFlash ? ' camera-drop-zone__evidence-capture--captured' : ''}`}
                    onClick={handleEvidenceClick}
                    title="Capturar fotograma como evidencia"
                    aria-label="Capturar evidencia"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                      <circle cx="12" cy="13" r="4" />
                    </svg>
                    {slotEvidencePhotos.length > 0 ? (
                      <span className="camera-drop-zone__evidence-capture-badge">{slotEvidencePhotos.length}</span>
                    ) : null}
                  </button>
                )}
                <button
                  type="button"
                  className="camera-drop-zone__remove"
                  onClick={() => onRemove(slot.id)}
                  title="Quitar cámara"
                >
                  ×
                </button>
              </div>
            </div>
            {streamVisualStatus === 'playing' && (
              <div className="camera-drop-zone__overlay-bottom">
                <span className="camera-drop-zone__time-pill">{playbackTimeLabel}</span>
              </div>
            )}
            <button
              type="button"
              className="camera-drop-zone__expand"
              onClick={() => onExpand(slot.id)}
              title="Ampliar video"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
              </svg>
            </button>
          </>
        )}
        {isExpanded && onEvidenceCapture && (streamVisualStatus === 'playing' || streamVisualStatus === 'loading') && (
          <button
            type="button"
            className={`video-expand-evidence${evidenceCaptureFlash ? ' video-expand-evidence--captured' : ''}`}
            onClick={handleEvidenceClick}
            title="Capturar fotograma como evidencia"
            aria-label="Capturar evidencia"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
          </button>
        )}
        {isExpanded && (
          <div className="video-expand-overlay-bottom">
            {streamVisualStatus !== 'loading' && (
              <span className={`video-expand-status-pill ${streamVisualStatus}`}>
                <span className="video-expand-status-dot" aria-hidden />
                {streamVisualStatus === 'playing'
                  ? 'PLAYING'
                  : streamVisualStatus === 'error' || streamVisualStatus === 'offline'
                    ? 'ERROR'
                    : 'READY'}
              </span>
            )}
            <span className="video-expand-time-pill">{playbackTimeLabel}</span>
          </div>
        )}
      </div>
    );

    return (
      <div className="camera-drop-zone camera-drop-zone--ready" data-slot-id={slot.id} onDragOver={handleDragOver} onDrop={handleDrop}>
        <div className="camera-drop-zone__content">
          {isExpanded && (
            <div
              className="camera-drop-zone__expanded-placeholder"
              onClick={() => onExpand(null)}
              role="button"
              tabIndex={0}
            >
              <span>Vista ampliada activa</span>
              <span className="camera-drop-zone__hint">Clic para volver</span>
            </div>
          )}
          <div
            ref={setInlineHostEl}
            className="camera-drop-zone__inline-player-host"
            style={isExpanded ? { display: 'none' } : undefined}
          >
            {streamVisualStatus === 'loading' && (
              <div className="camera-drop-zone__inline-loading">
                <XTAMSpinner variant="small" message={
                  networkHealth?.quality === 'offline'
                    ? 'Sin conexión al servidor de video'
                    : networkHealth?.quality === 'slow'
                      ? `Conexión lenta (ping: ${networkHealth.pingMs}ms)`
                      : 'Preparando grabación…'
                } />
              </div>
            )}
          </div>
          {!isExpanded && evidenceStripEmbedded}
        </div>
        {stableHostEl && createPortal(videoArea, stableHostEl)}
      </div>
    );
  }

  // READY sin playback todavía (race condition): muestra spinner en vez de negro.
  return (
    <div className="camera-drop-zone camera-drop-zone--preparing" data-slot-id={slot.id} onDragOver={handleDragOver} onDrop={handleDrop}>
      <div className="camera-drop-zone__content">
        <div className="camera-drop-zone__xtam-spinner-wrap">
          <XTAMSpinner variant="small" message="Preparando stream…" />
        </div>
        <div className="camera-drop-zone__info">
          <span className="camera-drop-zone__name">{slot.camera.name}</span>
        </div>
        <button type="button" className="camera-drop-zone__remove" onClick={() => onRemove(slot.id)} title="Quitar cámara">×</button>
        {evidenceStripOverlay}
      </div>
    </div>
  );
};

export default CameraDropZone;
